'use client';

import React, { useState } from 'react';
import Image from 'next/image';
import { TeamMember } from "../../lib/types";
import CardSection from "./CardSection";

interface TeamSectionProps {
  variant?: 'home' | 'about';
  teamMembers: TeamMember[];
  title?: string;
  body?: string;
}

interface ExtendedCardSectionItem {
  id: string | number;
  imageSrc: string;
  alt: string;
  title?: string;
  subtitle?: string;
  extraLine?: string;
  originalRole: string;
}

const getLastName = (fullName?: string) => {
  if (!fullName) {
    return '';
  }

  const trimmedName = fullName.trim();
  if (!trimmedName) {
    return '';
  }

  const nameParts = trimmedName.split(/\s+/);
  return nameParts[nameParts.length - 1].toLowerCase();
};

/**
 * Formats a role based on view mode, preserving acronyms
 * @param role - The role string to format
 * @param viewMode - 'card' for uppercase, 'list' for capitalize
 * @returns Formatted role string
 */
const formatRole = (role: string, viewMode: 'card' | 'list'): string => {
  if (!role) {
    return '';
  }

  const acronyms = ['CRM', 'CEO', 'CTO', 'CFO', 'COO', 'CMO', 'VP', 'SVP', 'EVP']; // Add more as needed
  const trimmedRole = role.trim();
  const upperRole = trimmedRole.toUpperCase();
  
  // Check if the entire role is an acronym
  if (acronyms.includes(upperRole)) {
    return upperRole;
  }
  
  // For card view: all uppercase
  if (viewMode === 'card') {
    return upperRole;
  }
  
  // For list view: capitalize first letter of each word, but preserve acronyms
  return trimmedRole
    .split(/\s+/)
    .map(word => {
      const upperWord = word.toUpperCase();
      // Check if this word is an acronym
      if (acronyms.includes(upperWord)) {
        return upperWord;
      }
      // Otherwise capitalize normally
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(' ');
};

export default function Team({ variant = 'home', teamMembers, title, body }: TeamSectionProps) {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  // Hide image on scroll
  React.useEffect(() => {
    const handleScroll = () => {
      if (hoveredIndex !== null) {
        setHoveredIndex(null);
      }
    };

    window.addEventListener('scroll', handleScroll, true);
    return () => {
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [hoveredIndex]);

  const cardWidth = 305;
  const cardHeight = 420;
  const cardPadding = 20;
  // Actual image dimensions inside the card (accounting for padding and text area)
  const imageWidth = cardWidth - (cardPadding * 2); // 280px
  const imageHeight = 296; // Fixed height from OverlappingCards

  const sortedTeamMembers = React.useMemo(() => {
    if (!teamMembers || teamMembers.length === 0) {
      return [];
    }

    return [...teamMembers].sort((teamMemberA, teamMemberB) => {
      const lastNameA = getLastName(teamMemberA.fullName);
      const lastNameB = getLastName(teamMemberB.fullName);

      if (lastNameA === lastNameB) {
        return (teamMemberA.fullName || '').localeCompare(teamMemberB.fullName || '');
      }

      return lastNameA.localeCompare(lastNameB);
    });
  }, [teamMembers]);

  const cardSectionItems = React.useMemo<ExtendedCardSectionItem[]>(() => {
    return sortedTeamMembers.map((m, i) => ({
      id: i,
      imageSrc: (m.profilePicture && m.profilePicture.length > 0) ? m.profilePicture : '/images/gallery/gallery-01.jpg',
      alt: m.fullName || 'Team member',
      title: variant === 'about' ? m.fullName : undefined,
      subtitle: variant === 'about' ? formatRole(m.role || '', 'card') : undefined,
      extraLine: m.extraLine,
      originalRole: m.role || '', // Keep original for list view formatting
    }));
  }, [sortedTeamMembers, variant]);

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    // Temporarily disabled on mobile
    if (typeof window !== 'undefined' && window.innerWidth < 1024) return;
    setMousePosition({ x: e.clientX, y: e.clientY });
  };

  const handleMouseEnter = (index: number) => {
    // Temporarily disabled on mobile
    if (typeof window !== 'undefined' && window.innerWidth < 1024) return;
    setHoveredIndex(index);
  };

  const handleMouseLeave = () => {
    // Temporarily disabled on mobile
    if (typeof window !== 'undefined' && window.innerWidth < 1024) return;
    setHoveredIndex(null);
  };

  const hoveredItem = hoveredIndex !== null ? cardSectionItems[hoveredIndex] : null;

  return (
    <>
      <CardSection
        items={cardSectionItems as any}
        title={title}
        body={body}
        defaultTitle="Who we are"
        defaultBody="Independent minds, collective impact."
        className={variant === 'about' ? 'team-section--plain' : ''}
        hoverBackgroundColor="#AAFA96"
        cardWidth={cardWidth}
        cardHeight={cardHeight}
        overlapPx={60}
        radiusPx={12}
        showViewToggle={variant === 'about'}
        showTitle={variant !== 'about'}
        bodyClassName={variant === 'about' ? 'heading-2' : 'body-xl-display'}
        renderListItem={(item, index) => {
          const extendedItem = item as ExtendedCardSectionItem;
          return (
            <div
              key={index}
              className="relative flex flex-col md:flex-row md:justify-between md:items-center pb-[1.5625rem] lg:pb-[3.25rem]"
              onMouseEnter={() => handleMouseEnter(index)}
              onMouseLeave={handleMouseLeave}
              onMouseMove={handleMouseMove}
            >
              <span className="card-title-lg">{extendedItem.title}</span>
              <span className="body-lg md:ml-4">{formatRole(extendedItem.originalRole, 'list')}</span>
            </div>
          );
        }}
      />
      {hoveredItem && (() => {
        // Check if mouse is 75% from the right of the page
        const windowWidth = typeof window !== 'undefined' ? window.innerWidth : 0;
        const isNearRightEdge = mousePosition.x > windowWidth * 0.75;
        
        // If near right edge, position image to the left of cursor
        const leftPosition = isNearRightEdge 
          ? `${mousePosition.x - imageWidth - 20}px`
          : `${mousePosition.x + 20}px`;
        
        return (
          <div
            className="fixed pointer-events-none z-50 transition-opacity duration-200 hidden md:block"
            style={{
              left: leftPosition,
              top: `${mousePosition.y - 20}px`,
              transform: 'translateY(-100%)',
            }}
            aria-hidden="true"
          >
          <div 
            className="relative rounded-xl overflow-hidden bg-[#FDFEF6]"
            style={{
              width: `${imageWidth}px`,
              height: `${imageHeight}px`,
              boxSizing: 'border-box',
            }}
          >
            <Image
              src={hoveredItem.imageSrc}
              alt={hoveredItem.alt}
              fill
              className="object-cover"
              sizes={`${imageWidth}px`}
            />
          </div>
        </div>
        );
      })()}
    </>
  );
}
